import { useParams } from "react-router-dom";
import { SimpleCard, SpinnerDefault, TitlelessCard } from "../../components";
import { useEffect, useState } from "react";
import TubingService from "../../services/TubingService";
import { showErrorMessage } from "../../app/tools";
import { TubingFlwBaselineModel } from "../../data/entities";
import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartSeries,
  ChartSeriesItem,
  ChartValueAxis,
  ChartValueAxisItem,
} from "@progress/kendo-react-charts";

interface EditState {
  position: string;
  wallThickness: string;
}

export function TubingMillCertPage() {
  const params = useParams();
  const id = parseInt(params["id"] ?? "0");

  const [controlPoints, setControlPoints] = useState<TubingFlwBaselineModel[]>([]);
  const [tubingLength, setTubingLength] = useState<number>(0);
  const [millCertFileName, setMillCertFileName] = useState<string | null>(null);
  const [chartPoints, setChartPoints] = useState<{ position: number; wallThickness: number }[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editState, setEditState] = useState<EditState>({ position: "", wallThickness: "" });

  const [isAdding, setIsAdding] = useState(false);
  const [newState, setNewState] = useState<EditState>({ position: "", wallThickness: "" });

  const [validationError, setValidationError] = useState<string | null>(null);

  const loadData = async () => {
    const [points, tubing] = await Promise.all([
      new TubingService().getTubingFlwBaseline(id),
      new TubingService().getTubingDetails(id.toString()),
    ]);

    const length = tubing?.length ?? 0;
    setTubingLength(length);
    setMillCertFileName(tubing?.millCertFileName ?? null);

    const sorted = [...(points ?? [])].sort((a, b) => a.position - b.position);
    setControlPoints(sorted);

    if (sorted.length > 0) {
      const chart = await new TubingService().getFlwBaselineChart(id);
      setChartPoints(chart ?? []);
    } else {
      setChartPoints([]);
    }

    setIsDataLoaded(true);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      showErrorMessage('Only PDF files are allowed.');
      e.target.value = '';
      return;
    }
    setShowSpinner(true);
    const ok = await new TubingService().uploadMillCert(id, file);
    if (ok) await loadData();
    e.target.value = '';
    setShowSpinner(false);
  };

  const handleView = async () => {
    const blob = await new TubingService().downloadMillCert(id);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const handleDeleteMillCert = async () => {
    setShowSpinner(true);
    const ok = await new TubingService().deleteMillCert(id);
    if (ok) await loadData();
    setShowSpinner(false);
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const validate = (position: number, wallThickness: number, excludeId?: number): string | null => {
    if (isNaN(position)) return "Position must be a valid number.";
    if (isNaN(wallThickness) || wallThickness <= 0) return "Wall thickness must be a positive number.";
    if (position < 0 || position > tubingLength)
      return `Position must be between 0 and ${tubingLength} m (tubing length).`;
    const duplicate = controlPoints.find((p) => p.position === position && p.id !== excludeId);
    if (duplicate) return `A control point at position ${position} already exists.`;
    return null;
  };

  const startEdit = (point: TubingFlwBaselineModel) => {
    setValidationError(null);
    setIsAdding(false);
    setEditingId(point.id);
    setEditState({ position: String(point.position), wallThickness: String(point.wallThickness) });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setValidationError(null);
  };

  const saveEdit = async () => {
    const position = parseFloat(editState.position);
    const wallThickness = parseFloat(editState.wallThickness);
    const error = validate(position, wallThickness, editingId!);
    if (error) { setValidationError(error); return; }

    setShowSpinner(true);
    setValidationError(null);
    const ok = await new TubingService().updateTubingBaselinePoint(id, editingId!, position, wallThickness);
    if (ok) {
      setEditingId(null);
      await loadData();
    }
    setShowSpinner(false);
  };

  const deletePoint = async (cpId: number) => {
    setShowSpinner(true);
    await new TubingService().deleteTubingBaselinePoint(id, cpId);
    await loadData();
    setShowSpinner(false);
  };

  const startAdd = () => {
    setValidationError(null);
    setEditingId(null);
    setNewState({ position: "", wallThickness: "" });
    setIsAdding(true);
  };

  const cancelAdd = () => {
    setIsAdding(false);
    setValidationError(null);
  };

  const saveAdd = async () => {
    const position = parseFloat(newState.position);
    const wallThickness = parseFloat(newState.wallThickness);
    const error = validate(position, wallThickness);
    if (error) { setValidationError(error); return; }

    setShowSpinner(true);
    setValidationError(null);
    const created = await new TubingService().addTubingBaselinePoint(id, position, wallThickness);
    if (created) {
      setNewState({ position: "", wallThickness: "" });
      await loadData();
    }
    setShowSpinner(false);
  };

  if (!isDataLoaded) return <SpinnerDefault />;

  return (
    <>
      <SimpleCard className="mb-3 p-0" title="Mill Cert PDF">
        <SpinnerDefault show={showSpinner} fullscreen={true} />

        {millCertFileName ? (
          <div className="d-flex align-items-center gap-3 p-3 bg-light rounded border">
            <i className="bi bi-file-earmark-pdf text-danger fs-3" />
            <div className="flex-grow-1">
              <div className="fw-semibold">{millCertFileName}</div>
              <div className="text-muted small">Mill Certificate PDF</div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={handleView}>
              View
            </button>
            <button className="btn btn-danger btn-sm" onClick={handleDeleteMillCert}>
              Delete
            </button>
          </div>
        ) : (
          <div className="d-flex align-items-center gap-3">
            <input
              type="file"
              accept=".pdf"
              className="form-control form-control-sm"
              style={{ maxWidth: "320px" }}
              onChange={handleUpload}
            />
          </div>
        )}
      </SimpleCard>

      <SimpleCard className="mb-3 p-0" title="FLW Baseline Control Points">
        <p>
          Define the tubing-level FLW baseline as control points (position and wall thickness).
          Linear interpolation between adjacent points defines the wall thickness at any position.
          Positions must be unique and within 0&ndash;{tubingLength} m.
        </p>

        {validationError && (
          <div className="alert alert-warning mb-3" role="alert">
            {validationError}
          </div>
        )}

        <table className="table table-hover mt-2">
          <thead>
            <tr>
              <th>Position (m)</th>
              <th>Wall Thickness</th>
              <th style={{ width: "160px" }}></th>
            </tr>
          </thead>
          <tbody>
            {controlPoints.map((point) =>
              editingId === point.id ? (
                <tr key={point.id}>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editState.position}
                      min={0}
                      max={tubingLength}
                      step="any"
                      onChange={(e) => setEditState((s) => ({ ...s, position: e.target.value }))}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editState.wallThickness}
                      min={0}
                      step="any"
                      onChange={(e) => setEditState((s) => ({ ...s, wallThickness: e.target.value }))}
                    />
                  </td>
                  <td>
                    <button className="btn btn-sm btn-primary me-1" onClick={saveEdit}>
                      Save
                    </button>
                    <button className="btn btn-sm btn-secondary" onClick={cancelEdit}>
                      Cancel
                    </button>
                  </td>
                </tr>
              ) : (
                <tr key={point.id}>
                  <td>{point.position}</td>
                  <td>{point.wallThickness}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-1"
                      onClick={() => startEdit(point)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deletePoint(point.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              )
            )}

            {isAdding && (
              <tr>
                <td>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="0"
                    value={newState.position}
                    min={0}
                    max={tubingLength}
                    step="any"
                    onChange={(e) => setNewState((s) => ({ ...s, position: e.target.value }))}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="0.0000"
                    value={newState.wallThickness}
                    min={0}
                    step="any"
                    onChange={(e) => setNewState((s) => ({ ...s, wallThickness: e.target.value }))}
                  />
                </td>
                <td>
                  <button className="btn btn-sm btn-success me-1" onClick={saveAdd}>
                    Add
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={cancelAdd}>
                    Cancel
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {!isAdding && editingId === null && (
          <div className="mb-3">
            <button className="btn btn-outline-primary" onClick={startAdd}>
              + Add Control Point
            </button>
          </div>
        )}
      </SimpleCard>

      {chartPoints.length > 0 && (
        <TitlelessCard className="mb-3">
          <Chart className="chart">
            <ChartCategoryAxis>
              <ChartCategoryAxisItem
                title={{ text: "Position (m)" }}
                categories={chartPoints.map((p) => p.position)}
                majorGridLines={{ visible: false }}
                majorTicks={{
                  visible: true,
                  step: Math.round(chartPoints.length / 10 / 10) * 10,
                }}
                labels={{
                  step: Math.round(chartPoints.length / 10 / 10) * 10,
                  format: "{0:N0}",
                }}
              />
            </ChartCategoryAxis>
            <ChartValueAxis>
              <ChartValueAxisItem
                title={{ text: "Wall Thickness" }}
                labels={{ visible: true, format: "{0:N4}" }}
                majorGridLines={{ visible: true }}
                minorGridLines={{ visible: false }}
                majorTicks={{ visible: false }}
                minorTicks={{ visible: false }}
                line={{ visible: false }}
              />
            </ChartValueAxis>
            <ChartSeries>
              <ChartSeriesItem
                name="Wall Thickness"
                data={chartPoints.map((p) => p.wallThickness)}
                type="line"
                color="#0D698B"
                width={1.5}
                markers={{ visible: false }}
              />
            </ChartSeries>
          </Chart>
        </TitlelessCard>
      )}
    </>
  );
}

export default TubingMillCertPage;
